/** @jsx h */
import { component, h, App } from 'literaljs';
import { playPop } from '../../utils/sounds';
import { copyToClipboard } from '../../utils/clipboard';
import { showToast } from '../../utils/toast';

// ─── Demo Components ───
const CounterDemo = component({
	name: 'CounterDemo2',
	state: { count: 0 },
	methods() {
		return {
			increment() { this.setState({ count: this.getState().count + 1 }); },
			decrement() { this.setState({ count: this.getState().count - 1 }); },
			reset() { this.setState({ count: 0 }); }
		};
	},
	render() {
		const { count } = this.getState();
		return (
			<div class="live-demo-counter">
				<div class="live-demo-counter-val">{count}</div>
				<div class="live-demo-counter-btns">
					<button class="ld-btn" events={{ click: () => this.decrement() }}>-</button>
					<button class="ld-btn -primary" events={{ click: () => this.increment() }}>+</button>
					<button class="ld-btn -ghost" events={{ click: () => this.reset() }}>Reset</button>
				</div>
			</div>
		);
	}
});

const StoreDemo = component({
	name: 'StoreDemo2',
	state: { clicks: 0 },
	methods() {
		return {
			toggleMenu() {
				const { showMenu } = this.getStore();
				this.setStore({ showMenu: !showMenu });
				this.setState({ clicks: this.getState().clicks + 1 });
			}
		};
	},
	render() {
		const { showMenu } = this.getStore();
		const { clicks } = this.getState();
		return (
			<div class="live-demo-store">
				<div class="live-demo-store-status">
					<span class={"ld-indicator" + (showMenu ? " -on" : "")}></span>
					Menu: {showMenu ? 'OPEN' : 'CLOSED'}
				</div>
				{showMenu && (
					<div class="live-demo-store-items">
						<div class="ld-menu-item">▸ Dashboard</div>
						<div class="ld-menu-item">▸ Settings</div>
						<div class="ld-menu-item">▸ Profile</div>
					</div>
				)}
				<button class="ld-btn -primary" events={{ click: () => this.toggleMenu() }}>
					{showMenu ? 'Close Menu' : 'Open Menu'}
				</button>
				<div class="live-demo-store-note">{clicks} toggle{clicks !== 1 ? 's' : ''}</div>
			</div>
		);
	}
});

const EventsDemo = component({
	name: 'EventsDemo2',
	state: { on: false, hover: false, clicks: 0 },
	methods() {
		return {
			activate() { this.setState({ on: true, clicks: this.getState().clicks + 1 }); },
			deactivate() { this.setState({ on: false }); },
			setHover(v) { this.setState({ hover: v }); }
		};
	},
	render() {
		const { on, hover, clicks } = this.getState();
		return (
			<div class="live-demo-events">
				<button
					class={"ld-btn -lg" + (on ? " -on" : "") + (hover ? " -hover" : "")}
					events={{
						click: () => on ? this.deactivate() : this.activate(),
						mouseenter: () => this.setHover(true),
						mouseleave: () => this.setHover(false)
					}}
				>
					{on ? '● ACTIVE' : '○ Click Me'}
				</button>
				<div class="live-demo-events-stats">
					<span class={on ? "ld-stat -on" : "ld-stat"}>Status: {on ? 'ON' : 'OFF'}</span>
					<span>Clicks: {clicks}</span>
					<span>Hover: {hover ? 'YES' : 'NO'}</span>
				</div>
			</div>
		);
	}
});

const LifecycleDemo = component({
	name: 'LifecycleDemo2',
	state: { active: false, mountCount: 0 },
	methods() {
		return {
			toggle() { this.setState({ active: !this.getState().active }); }
		};
	},
	mounted() {
		this.setState({ mountCount: this.getState().mountCount + 1 });
	},
	unmounted() {
		this.setState({ active: false });
	},
	render() {
		const { active, mountCount } = this.getState();
		return (
			<div class="live-demo-lifecycle">
				<div class="live-demo-lifecycle-stats">
					<span>Mounts: {mountCount}</span>
					<span class={active ? "ld-stat -on" : "ld-stat"}>{active ? 'MOUNTED' : 'UNMOUNTED'}</span>
				</div>
				<button class="ld-btn -primary" events={{ click: () => this.toggle() }}>
					{active ? 'Unmount Child' : 'Mount Child'}
				</button>
				{active && (
					<div class="live-demo-lifecycle-box">
						<span class="ld-pulse"></span> Child component is alive
					</div>
				)}
			</div>
		);
	}
});

// ─── NEW: Props Demo ───
const PropsDemo = component({
	name: 'PropsDemo2',
	props: ['initialName', 'initialColor'],
	state: { name: '', color: '' },
	methods() {
		return {
			updateName(name) { this.setState({ name }); },
			updateColor(color) { this.setState({ color }); }
		};
	},
	mounted() {
		this.setState({
			name: this.props.initialName || 'World',
			color: this.props.initialColor || 'lavender'
		});
	},
	render() {
		const { name, color } = this.getState();
		const colorMap = {
			lavender: 'var(--lavender)',
			blossom: 'var(--blossom)',
			mint: 'var(--mint)',
			sky: 'var(--sky)',
			peach: 'var(--peach)'
		};
		return (
			<div class="live-demo-props">
				<div class="live-demo-props-display" style={`color: ${colorMap[color] || colorMap.lavender}`}>
					Hello, {name || 'World'}!
				</div>
				<div class="live-demo-props-controls">
					<input
						type="text"
						value={name}
						placeholder="Enter name..."
						events={{ input: (e) => this.updateName(e.target.value) }}
						class="ld-input"
					/>
					<div class="ld-color-btns">
						{['lavender', 'blossom', 'mint', 'sky', 'peach'].map(c => (
							<button
								key={c}
								class={"ld-color-btn" + (color === c ? " -active" : "")}
								style={`background: ${colorMap[c]}`}
								events={{ click: () => this.updateColor(c) }}
							/>
						))}
					</div>
				</div>
			</div>
		);
	}
});

// ─── NEW: Keys Demo ───
const KeysDemo = component({
	name: 'KeysDemo2',
	state: { items: [
		{ id: 'a', text: 'First Item' },
		{ id: 'b', text: 'Second Item' },
		{ id: 'c', text: 'Third Item' }
	] },
	methods() {
		return {
			reverse() {
				this.setState({ items: [...this.getState().items].reverse() });
			},
			shuffle() {
				const items = [...this.getState().items];
				for (let i = items.length - 1; i > 0; i--) {
					const j = Math.floor(Math.random() * (i + 1));
					[items[i], items[j]] = [items[j], items[i]];
				}
				this.setState({ items });
			}
		};
	},
	render() {
		const { items } = this.getState();
		return (
			<div class="live-demo-keys">
				<div class="live-demo-keys-list">
					{items.map((item, idx) => (
						<div key={item.id} class="ld-key-item">
							<span class="ld-key-id">{item.id}</span>
							<span class="ld-key-text">{item.text}</span>
							<span class="ld-key-idx">#{idx + 1}</span>
						</div>
					))}
				</div>
				<div class="live-demo-keys-btns">
					<button class="ld-btn" events={{ click: () => this.reverse() }}>Reverse</button>
					<button class="ld-btn -primary" events={{ click: () => this.shuffle() }}>Shuffle</button>
				</div>
			</div>
		);
	}
});

// ─── NEW: Conditional Rendering Demo ───
const ConditionalDemo = component({
	name: 'ConditionalDemo2',
	state: { view: 'list', loading: false },
	methods() {
		return {
			setView(view) { this.setState({ view }); },
			toggleLoading() {
				this.setState({ loading: true });
				setTimeout(() => this.setState({ loading: false }), 1500);
			}
		};
	},
	render() {
		const { view, loading } = this.getState();
		return (
			<div class="live-demo-conditional">
				<div class="ld-view-tabs">
					{['list', 'grid', 'details'].map(v => (
						<button
							key={v}
							class={"ld-view-tab" + (view === v ? " -active" : "")}
							events={{ click: () => this.setView(v) }}
						>
							{v.charAt(0).toUpperCase() + v.slice(1)}
						</button>
					))}
				</div>
				{loading ? (
					<div class="ld-loading">Loading...</div>
				) : (
					<div class="ld-view-content">
						{view === 'list' && (
							<div class="ld-view-list">
								<div class="ld-view-row">Item One</div>
								<div class="ld-view-row">Item Two</div>
								<div class="ld-view-row">Item Three</div>
							</div>
						)}
						{view === 'grid' && (
							<div class="ld-view-grid">
								<div class="ld-view-cell">A</div>
								<div class="ld-view-cell">B</div>
								<div class="ld-view-cell">C</div>
								<div class="ld-view-cell">D</div>
							</div>
						)}
						{view === 'details' && (
							<div class="ld-view-details">
								<h4>Details View</h4>
								<p>Showing detailed information with conditional rendering.</p>
							</div>
						)}
					</div>
				)}
				<button class="ld-btn -ghost" events={{ click: () => this.toggleLoading() }}>
					Simulate Loading
				</button>
			</div>
		);
	}
});

// ─── NEW: Lists & Arrays Demo ───
const ListsDemo = component({
	name: 'ListsDemo2',
	state: {
		todos: [
			{ id: 1, text: 'Learn LiteralJS', done: true },
			{ id: 2, text: 'Build something cool', done: false },
			{ id: 3, text: 'Share with friends', done: false }
		],
		newTodo: ''
	},
	methods() {
		return {
			toggleTodo(id) {
				const todos = this.getState().todos.map(t =>
					t.id === id ? { ...t, done: !t.done } : t
				);
				this.setState({ todos });
			},
			addTodo() {
				const { newTodo, todos } = this.getState();
				if (!newTodo.trim()) return;
				this.setState({
					todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
					newTodo: ''
				});
			},
			setNewTodo(text) { this.setState({ newTodo: text }); },
			removeTodo(id) {
				this.setState({
					todos: this.getState().todos.filter(t => t.id !== id)
				});
			}
		};
	},
	render() {
		const { todos, newTodo } = this.getState();
		return (
			<div class="live-demo-lists">
				<div class="ld-todo-input-row">
					<input
						type="text"
						value={newTodo}
						placeholder="Add a todo..."
						class="ld-input"
						events={{
							input: (e) => this.setNewTodo(e.target.value),
							keydown: (e) => e.key === 'Enter' && this.addTodo()
						}}
					/>
					<button class="ld-btn -primary" events={{ click: () => this.addTodo() }}>Add</button>
				</div>
				<div class="ld-todo-list">
					{todos.map(todo => (
						<div key={todo.id} class={"ld-todo-item" + (todo.done ? " -done" : "")}>
							<input
								type="checkbox"
								checked={todo.done}
								events={{ change: () => this.toggleTodo(todo.id) }}
							/>
							<span class="ld-todo-text">{todo.text}</span>
							<button
								class="ld-todo-del"
								events={{ click: () => this.removeTodo(todo.id) }}
							>
								×
							</button>
						</div>
					))}
				</div>
				<div class="ld-todo-stats">
					{todos.filter(t => t.done).length} / {todos.length} completed
				</div>
			</div>
		);
	}
});

// ─── NEW: Forms Demo ───
const FormsDemo = component({
	name: 'FormsDemo2',
	state: {
		email: '',
		message: '',
		priority: 'normal',
		submitted: false
	},
	methods() {
		return {
			setEmail(email) { this.setState({ email, submitted: false }); },
			setMessage(message) { this.setState({ message, submitted: false }); },
			setPriority(priority) { this.setState({ priority, submitted: false }); },
			submit() {
				const { email, message } = this.getState();
				if (email && message) {
					this.setState({ submitted: true });
				}
			},
			reset() {
				this.setState({ email: '', message: '', priority: 'normal', submitted: false });
			}
		};
	},
	render() {
		const { email, message, priority, submitted } = this.getState();
		return (
			<div class="live-demo-forms">
				{submitted ? (
					<div class="ld-form-success">
						<div class="ld-form-success-icon">✓</div>
						<div class="ld-form-success-text">Form submitted!</div>
						<div class="ld-form-success-detail">
							Email: {email}<br/>
							Priority: {priority}
						</div>
						<button class="ld-btn" events={{ click: () => this.reset() }}>Reset</button>
					</div>
				) : (
					<div class="ld-form-fields">
						<div class="ld-form-group">
							<label>Email</label>
							<input
								type="email"
								value={email}
								placeholder="you@example.com"
								class="ld-input"
								events={{ input: (e) => this.setEmail(e.target.value) }}
							/>
						</div>
						<div class="ld-form-group">
							<label>Message</label>
							<textarea
								value={message}
								placeholder="Your message..."
								class="ld-input"
								rows="2"
								events={{ input: (e) => this.setMessage(e.target.value) }}
							/>
						</div>
						<div class="ld-form-group">
							<label>Priority</label>
							<div class="ld-priority-btns">
								{['low', 'normal', 'high'].map(p => (
									<button
										key={p}
										class={"ld-priority-btn" + (priority === p ? " -active" : "")}
										events={{ click: () => this.setPriority(p) }}
									>
										{p}
									</button>
								))}
							</div>
						</div>
						<button
							class="ld-btn -primary"
							events={{ click: () => this.submit() }}
							disabled={!email || !message}
						>
							Submit
						</button>
					</div>
				)}
			</div>
		);
	}
});

// ─── NEW: Best Practices Demo ───
const BestPracticesDemo = component({
	name: 'BestPracticesDemo2',
	state: {
		query: '',
		filter: 'all',
		debouncedQuery: ''
	},
	methods() {
		return {
			setQuery(query) {
				this.setState({ query });
				// Debounce pattern
				if (this._debounceTimer) clearTimeout(this._debounceTimer);
				this._debounceTimer = setTimeout(() => {
					this.setState({ debouncedQuery: query });
				}, 300);
			},
			setFilter(filter) { this.setState({ filter }); }
		};
	},
	unmounted() {
		if (this._debounceTimer) clearTimeout(this._debounceTimer);
	},
	render() {
		const { query, filter, debouncedQuery } = this.getState();
		const items = [
			{ id: 1, name: 'Use keys for lists', category: 'rendering' },
			{ id: 2, name: 'Keep state minimal', category: 'state' },
			{ id: 3, name: 'Clean up in unmounted', category: 'lifecycle' },
			{ id: 4, name: 'Use functional setState', category: 'state' },
			{ id: 5, name: 'Avoid deep nesting', category: 'structure' }
		];
		const filtered = items.filter(item => {
			const matchesFilter = filter === 'all' || item.category === filter;
			const matchesQuery = !debouncedQuery ||
				item.name.toLowerCase().includes(debouncedQuery.toLowerCase());
			return matchesFilter && matchesQuery;
		});
		return (
			<div class="live-demo-practices">
				<div class="ld-practice-search">
					<input
						type="text"
						value={query}
						placeholder="Search practices..."
						class="ld-input"
						events={{ input: (e) => this.setQuery(e.target.value) }}
					/>
				</div>
				<div class="ld-practice-filters">
					{['all', 'rendering', 'state', 'lifecycle', 'structure'].map(f => (
						<button
							key={f}
							class={"ld-practice-filter" + (filter === f ? " -active" : "")}
							events={{ click: () => this.setFilter(f) }}
						>
							{f}
						</button>
					))}
				</div>
				<div class="ld-practice-list">
					{filtered.map(item => (
						<div key={item.id} class="ld-practice-item">
							<span class="ld-practice-name">{item.name}</span>
							<span class="ld-practice-cat">{item.category}</span>
						</div>
					))}
					{filtered.length === 0 && (
						<div class="ld-practice-empty">No matches found</div>
					)}
				</div>
			</div>
		);
	}
});

const demoComponents = {
	counter: CounterDemo,
	store: StoreDemo,
	events: EventsDemo,
	lifecycle: LifecycleDemo,
	props: PropsDemo,
	keys: KeysDemo,
	conditional: ConditionalDemo,
	lists: ListsDemo,
	forms: FormsDemo,
	bestpractices: BestPracticesDemo
};

const isolatedDemos = ['store'];

// ─── DemoCard: bulletproof code/live toggle ───
const DemoCard = component({
	name: 'DemoCard',
	props: ['tag', 'title', 'code', 'demoType', 'containerId', 'description', 'onCopy'],
	state: { mode: 'code' },
	methods() {
		return {
			switchToLive() {
				if (this.getState().mode === 'live') return;
				playPop();
				this._unmountDemo();
				this.setState({ mode: 'live' });
				this._mountTimer = setTimeout(() => {
					if (this.getState().mode === 'live') {
						this._mountDemo();
					}
				}, 0);
			},
			switchToCode() {
				if (this.getState().mode === 'code') return;
				playPop();
				if (this._mountTimer) clearTimeout(this._mountTimer);
				this._unmountDemo();
				this.setState({ mode: 'code' });
			},
			_mountDemo() {
				if (this._app) return;
				const { demoType } = this.props;
				const DemoComponent = demoComponents[demoType];
				if (!DemoComponent) return;
				if (isolatedDemos.includes(demoType)) {
					const container = document.getElementById(this.props.containerId);
					if (!container) return;
					container.innerHTML = '';
					const store = demoType === 'store' ? { showMenu: false } : {};
					this._app = new App(DemoComponent, store);
					this._app.mount(this.props.containerId);
				}
			},
			_unmountDemo() {
				if (this._app) {
					this._app.unmount();
					this._app = null;
				}
				const container = document.getElementById(this.props.containerId);
				if (container) container.innerHTML = '';
			}
		};
	},
	unmounted() {
		if (this._mountTimer) clearTimeout(this._mountTimer);
		if (this._toastTimer) clearTimeout(this._toastTimer);
		if (this._toastRemoveTimer) clearTimeout(this._toastRemoveTimer);
		this._unmountDemo();
	},
	render() {
		const { mode } = this.getState();
		const { tag, title, code, containerId, description, demoType } = this.props;
		const DemoComponent = demoComponents[demoType];
		const needsIsolation = isolatedDemos.includes(demoType);

		return (
			<div class="doc-card">
				<div class="doc-header">
					<span class="doc-tag">{tag}</span>
								<h2>{title}</h2>
					<div class="doc-tabs">
						<button
							class={"doc-tab" + (mode === 'code' ? " -active" : "")}
							events={{ click: () => this.switchToCode() }}
						>Code</button>
						<button
							class={"doc-tab" + (mode === 'live' ? " -active" : "")}
							events={{ click: () => this.switchToLive() }}
						>Live</button>
					</div>
				</div>
				{mode === 'code' ? (
					<div class="code-block code-block-wrapper">
						<button
							class={`copy-btn ${this.getState().copiedCode ? '-copied' : ''}`}
							events={{ click: () => { if (this.props.onCopy) this.props.onCopy(code, title); } }}
							aria-label="Copy code to clipboard"
						>
							{this.getState().copiedCode ? '✅ Copied!' : '📋 Copy'}
						</button>
						<div class="code-body">
							<pre><code class="language-javascript">{code}</code></pre>
						</div>
					</div>
				) : (
					<div class="live-demo-stage">
						{needsIsolation ? (
							<div id={containerId} class="live-demo-container"></div>
						) : (
							<div class="live-demo-container">
								{demoType === 'props' ? (
									<PropsDemo initialName="LiteralJS" initialColor="lavender" />
								) : (
									<DemoComponent />
								)}
							</div>
						)}
					</div>
				)}
				<p class="doc-desc">{description}</p>
			</div>
		);
	}
});

// ─── DocsPage ───
export default component({
	name: 'DocsPage',
	state: { },
	methods() {
		return {
			copyCode(text, label) {
				copyToClipboard(text,
					() => {
						this.setState({ copiedCode: true });
						showToast(`Copied ${label} to clipboard`, { type: 'success' });
						if (this._copyResetTimer) clearTimeout(this._copyResetTimer);
						this._copyResetTimer = setTimeout(() => {
							this.setState({ copiedCode: false });
						}, 2000);
					},
					() => showToast(`Failed to copy ${label}`, { type: 'error' })
				);
			},
			setupScrollSpy() {
				const sections = document.querySelectorAll('.doc-anchor');
				let scrollRafId = null;
				this._onScroll = () => {
					if (scrollRafId) return;
					scrollRafId = requestAnimationFrame(() => {
						scrollRafId = null;
						const viewportCenter = window.scrollY + window.innerHeight / 2;
						let closestSection = null;
						let closestDistance = Infinity;
						sections.forEach(section => {
							const rect = section.getBoundingClientRect();
							const sectionCenter = window.scrollY + rect.top + rect.height / 2;
							const distance = Math.abs(viewportCenter - sectionCenter);
							if (distance < closestDistance) {
								closestDistance = distance;
								closestSection = section;
							}
						});
						if (closestSection && closestSection.id !== this.getStore().docsActiveSection) {
							this.setStore({ docsActiveSection: closestSection.id });
						}
					});
				};
				window.addEventListener('scroll', this._onScroll, { passive: true });
			},
		};
	},
	mounted() {
		this.setupScrollSpy();
	},
	unmounted() {
		if (this._observer) {
			this._observer.disconnect();
		}
		if (this._onScroll) {
			window.removeEventListener('scroll', this._onScroll);
		}
	},
	render() {
		return (
			<div class="page-docs">
				<section class="docs">
					<div class="docs-container">
						<div class="docs-main">
							<div class="section-header">
								<h1 class="docs-title">API Reference</h1>
								<p class="section-subtitle">Zero dependency. Modern ES2018+. Toggle between code and live demos.</p>
							</div>

							<div class="doc-grid">
						{/* ─── Core API Section ─── */}
						<div id="component" class="doc-anchor">
						<DemoCard
							tag="Core"
							title="component()"
							demoType="counter"
							containerId="demo-counter"
							key="card-counter"
							onCopy={(code, title) => this.copyCode(code, title)}
							code={`// Define a reusable component with state and methods
const Counter = component({
  name: 'Counter',
  state: { count: 0 },
  methods() {
    return {
      increment() {
        // Use functional update for state based on previous value
        this.setState(s => ({ count: s.count + 1 }));
      }
    };
  },
  render() {
    const { count } = this.getState();
    return (
      <div>
        <p>Count: {count}</p>
        <button events={{ click: () => this.increment() }}>
          +
        </button>
      </div>
    );
  }
});

// JSX syntax (requires /** @jsx h */ pragma)
// Or use h() directly: h('div', null, 'Count: ', count)`}
							description="Returns a factory function. Sibling components are auto-keyed by render position in v8.0.5. Use the /** @jsx h */ pragma to enable JSX."
						/>
						</div>

						<div id="app" class="doc-anchor">
						<DemoCard
							tag="Mount"
							title="App"
							demoType="store"
							containerId="demo-store"
							key="card-store"
							onCopy={(code, title) => this.copyCode(code, title)}
							code={`// Create an isolated app instance with optional store
const store = { showMenu: false };
const app = new App(RootComponent, store);

// Mount to a DOM element by ID
app.mount('root');

// Access store from any component in this app
const { showMenu } = this.getStore();
this.setStore({ showMenu: !showMenu });

// Each App is fully isolated — state, events, lifecycle
// are scoped to that instance only.`}
							description="Isolated app container. Each App has its own state, events, and lifecycle. Perfect for embedding multiple independent apps on the same page."
						/>
						</div>

						<div id="lifecycle" class="doc-anchor">
						<DemoCard
							tag="Lifecycle"
							title="mounted / unmounted"
							demoType="lifecycle"
							containerId="demo-lifecycle"
							key="card-lifecycle"
							onCopy={(code, title) => this.copyCode(code, title)}
							code={`// Lifecycle hooks for setup and teardown
component({
  name: 'Canvas',
  state: { active: false },

  // Called once after first render to DOM
  mounted() {
    this._initCanvas();
    this._startAnimationLoop();
  },

  // Called when component is removed from DOM
  unmounted() {
    this._cleanupCanvas();
    this._stopAnimationLoop();
    // Always clean up timers, listeners, subscriptions
  },

  render() {
    return <canvas id="c" />;
  }
});`}
							description="Lifecycle hooks are queued and flushed after each render. Use unmounted to clean up timers and listeners to prevent memory leaks."
						/>
						</div>

						<div id="state" class="doc-anchor">
						<DemoCard
							tag="State"
							title="getState / setState"
							demoType="counter"
							containerId="demo-state"
							key="card-state"
							code={`// Read current state (always returns a fresh object)
const { count } = this.getState();

// Shallow merge update
this.setState({ count: count + 1 });

// Functional update — safer when next state depends on previous
this.setState(s => ({ count: s.count + 1 }));

// Reset to initial values
this.setState({ count: 0 });

// Callback fires after render completes
this.setState({ count: 5 }, () => {
  console.log('Rendered with count = 5');
});`}
							description="Shallow merge on setState. Functional updates supported for state derived from previous state. Callback fires after render completes."
						/>
						</div>

						<div id="store" class="doc-anchor">
						<DemoCard
							tag="Store"
							title="getStore / setStore"
							demoType="store"
							containerId="demo-store2"
							key="card-store2"
							code={`// Initialize app-level store when creating App
const store = { showMenu: false, theme: 'dark' };
const app = new App(RootComponent, store);

// Read from store in any component
const { showMenu, theme } = this.getStore();

// Update store — all components in the same App re-render
this.setStore({ showMenu: !showMenu });

// Functional updates work here too
this.setStore(s => ({ theme: s.theme === 'dark' ? 'light' : 'dark' }));

// Store is shared across all components in one App instance`}
							description="App-level store. All components in the same App instance receive updates when store changes. Use for global UI state like themes, menus, auth."
						/>
						</div>

						<div id="events" class="doc-anchor">
						<DemoCard
							tag="Events"
							title="events prop"
							demoType="events"
							containerId="demo-events"
							key="card-events"
							code={`// Attach event handlers via the events prop
<button events={{
  click: () => this.setState({ on: true }),
  mouseenter: () => this.setState({ hover: true }),
  mouseleave: () => this.setState({ hover: false }),
  dblclick: () => this.setState({ on: false })
}}>
  Interactive
</button>

// All standard DOM events supported
// WeakMap-based event delegation for performance
// Boolean flag short-circuit for zero-cost dispatch`}
							description="WeakMap event delegation. Boolean flag short-circuit for zero-cost dispatch. All standard DOM events are supported via the events prop."
						/>
						</div>

						{/* Auto Keys — code only, no demo needed */}
						<div id="autokeys" class="doc-anchor">
						<div class="card card-interactive doc-card">
							<div class="doc-header">
								<span class="doc-tag">v8.0.5</span>
									<h2>Auto-Keyed Siblings</h2>
							<button
								class={`copy-btn ${this.getState().copiedCode ? '-copied' : ''}`}
								events={{ click: () => this.copyCode(
									`// v8.0.5: Siblings auto-keyed by render position\n<div>\n  <Counter />  {/* key: __auto_0__ */}\n  <Counter />  {/* key: __auto_1__ */}\n</div>\n\n// Explicit keys still work for reordering\n<div>\n  <Item key="a" data={a} />\n  <Item key="b" data={b} />\n</div>`,
									'Auto-Keyed Siblings'
								)}}
								aria-label="Copy code to clipboard"
							>
								{this.getState().copiedCode ? '✅ Copied!' : '📋 Copy'}
							</button>
							</div>
							<div class="code-block">
								<div class="code-body">
									<pre><code class="language-javascript">{`// v8.0.5: Siblings auto-keyed by render position
<div>
  <Counter />  {/* key: __auto_0__ */}
  <Counter />  {/* key: __auto_1__ */}
</div>

// Explicit keys still work for reordering
<div>
  <Item key="a" data={a} />
  <Item key="b" data={b} />
</div>`}</code></pre>
								</div>
							</div>
							<p class="doc-desc">v8.0.5 auto-keys sibling components by render position. State isolation is now the default. Explicit keys still supported for reordering and list diffing.</p>
						</div>
						</div>

						{/* ─── Components Section ─── */}
						<div id="props" class="doc-anchor">
						<DemoCard
							tag="Components"
							title="Props"
							demoType="props"
							containerId="demo-props"
							key="card-props"
							code={`// Define which props the component accepts
const Greeting = component({
  name: 'Greeting',
  props: ['name', 'color'],  // Declare expected props

  render() {
    // Access via this.props
    const { name, color } = this.props;
    return (
      <h1 style={{ color }}>
        Hello, {name || 'World'}!
      </h1>
    );
  }
});

// Pass props when instantiating
<Greeting name="LiteralJS" color="var(--lavender)" />

// Or with h():
// h(Greeting, { name: 'LiteralJS', color: 'var(--lavender)' })`}
							description="Props are read-only values passed from parent to child. Declare them in the props array for clarity. Use for configuration and data flow down the component tree."
						/>
						</div>

						<div id="keys" class="doc-anchor">
						<DemoCard
							tag="Components"
							title="Keys"
							demoType="keys"
							containerId="demo-keys"
							key="card-keys"
							code={`// Use keys to help the diff engine track items
// Essential for lists that reorder, filter, or animate

const List = component({
  name: 'List',
  state: { items: [
    { id: 'a', text: 'First' },
    { id: 'b', text: 'Second' }
  ]},

  render() {
    const { items } = this.getState();
    return (
      <ul>
        {items.map(item => (
          // Always use a stable unique key
          <li key={item.id}>{item.text}</li>
        ))}
      </ul>
    );
  }
});

// Rules:
// - Keys must be unique among siblings
// - Use stable IDs, not array indices
// - Auto-keys work for static siblings`}
							description="Manual keys give you control over list diffing. Always use stable unique identifiers. Keys enable efficient reordering, filtering, and animation of list items."
						/>
						</div>

						<div id="conditional" class="doc-anchor">
						<DemoCard
							tag="Components"
							title="Conditional Rendering"
							demoType="conditional"
							containerId="demo-conditional"
							key="card-conditional"
							code={`// Use JavaScript expressions for conditional UI

component({
  name: 'Dashboard',
  state: { loading: true, error: null, data: null },

  render() {
    const { loading, error, data } = this.getState();

    // Early return pattern
    if (loading) return <div>Loading...</div>;
    if (error)   return <div class="error">{error}</div>;

    return (
      <div>
        {/* Ternary for inline conditions */}
        {data ? (
          <DataTable rows={data} />
        ) : (
          <EmptyState />
        )}

        {/* Logical AND for simple show/hide */}
        {data.length > 0 && <Pagination />}

        {/* Multiple conditions */}
        {data.length === 0 ? (
          <NoResults />
        ) : data.length > 100 ? (
          <VirtualList items={data} />
        ) : (
          <SimpleList items={data} />
        )}
      </div>
    );
  }
});`}
							description="Use standard JavaScript conditionals — ternary, logical AND, or early returns. The diff engine efficiently adds and removes elements based on conditional expressions."
						/>
						</div>

						<div id="lists" class="doc-anchor">
						<DemoCard
							tag="Components"
							title="Lists & Arrays"
							demoType="lists"
							containerId="demo-lists"
							key="card-lists"
							code={`// Render arrays with map() — returns virtual nodes

component({
  name: 'TodoList',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Build an app', done: false }
    ]
  },

  render() {
    const { todos } = this.getState();

    return (
      <div>
        {/* Always provide a key for list items */}
        <ul>
          {todos.map(todo => (
            <li
              key={todo.id}
              class={todo.done ? 'done' : ''}
            >
              <input
                type="checkbox"
                checked={todo.done}
                events={{
                  change: () => this.toggleTodo(todo.id)
                }}
              />
              {todo.text}
            </li>
          ))}
        </ul>

        {/* Filter and transform inline */}
        <p>
          {todos.filter(t => !t.done).length} remaining
        </p>
      </div>
    );
  }
});`}
							description="Use Array.map() to render lists. Always provide a stable key prop for efficient diffing. Filtering, sorting, and transforming arrays works seamlessly inline in JSX."
						/>
						</div>

						{/* ─── Patterns Section ─── */}
						<div id="forms" class="doc-anchor">
						<DemoCard
							tag="Patterns"
							title="Forms"
							demoType="forms"
							containerId="demo-forms"
							key="card-forms"
							code={`// Controlled form components with state

component({
  name: 'ContactForm',
  state: {
    email: '',
    message: '',
    priority: 'normal',
    submitted: false
  },

  methods() {
    return {
      // Handler for text inputs
      setField(field, value) {
        this.setState({ [field]: value, submitted: false });
      },

      // Form submission
      handleSubmit() {
        const { email, message } = this.getState();
        if (email && message) {
          this.setState({ submitted: true });
          // Send to API, validate, etc.
        }
      },

      // Reset form
      reset() {
        this.setState({
          email: '', message: '',
          priority: 'normal', submitted: false
        });
      }
    };
  },

  render() {
    const { email, message, priority, submitted } = this.getState();

    return (
      <form events={{ submit: (e) => { e.preventDefault(); this.handleSubmit(); } }}>
        <input
          type="email"
          value={email}
          placeholder="you@example.com"
          events={{ input: (e) => this.setField('email', e.target.value) }}
        />
        <textarea
          value={message}
          events={{ input: (e) => this.setField('message', e.target.value) }}
        />
        <select
          value={priority}
          events={{ change: (e) => this.setField('priority', e.target.value) }}
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
        </select>
        <button type="submit">Submit</button>
        {submitted && <div>Thank you!</div>}
      </form>
    );
  }
});`}
							description="Forms use controlled components — state is the single source of truth. Handle input, textarea, and select with onInput/onChange events. Always call e.preventDefault() on form submit."
						/>
						</div>

						<div id="bestpractices" class="doc-anchor">
						<DemoCard
							tag="Patterns"
							title="Best Practices"
							demoType="bestpractices"
							containerId="demo-bestpractices"
							key="card-bestpractices"
							code={`// Tips and common patterns for LiteralJS

// 1. Keep state minimal — derive don't duplicate
const fullName = \`\${first} \${last}\`; // Don't store in state

// 2. Use functional setState when depending on prev state
this.setState(s => ({ count: s.count + 1 }));

// 3. Clean up side effects in unmounted()
unmounted() {
  if (this._timer) clearInterval(this._timer);
  if (this._listener) window.removeEventListener('resize', this._listener);
}

// 4. Debounce expensive operations
setQuery(q) {
  this.setState({ query: q });
  clearTimeout(this._debounce);
  this._debounce = setTimeout(() => {
    this.setState({ debouncedQuery: q });
  }, 300);
}

// 5. Use keys for any dynamic list
// 6. Prefer props over deep state drilling
// 7. Split large components into smaller ones
// 8. Use store for truly global state only`}
							description="Proven patterns for maintainable LiteralJS apps. Keep state minimal, clean up side effects, debounce inputs, and use keys for dynamic lists. Store is for global state only."
						/>
							</div>
						</div>
					</div>


					<div class="docs-footer-note">
						<p>Full source on <a href="https://gitlab.com/mikeysax/LiteralJS" target="_blank">GitLab</a> — MIT License</p>
					</div>
				</div>
			</section>
			</div>
		);
	}
});
